import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { todayInIndia } from '../../utils/formatTime';
import { ElectionCategory } from '../../constants/categories';

export interface PromoCodeItem {
  id: string;
  code: string;
  discount_type: 'flat' | 'percentage';
  discount_value: string | number;
  applicable_plan_ids: string[];
  max_usage_count: number | null;
  per_user_limit: number;
  expiry_date: string;
  is_active: boolean;
  usage_count: number;
  created_at?: string;
  updated_at?: string;

  // Partner Phase One & Phase Nine: set on a partner-made or custom code, null on an owner-made one.
  // These can never be edited or deleted here — only activated/deactivated.
  partner_id: string | null;
  channel?: 'creator' | 'channel_partner' | null;
  category: ElectionCategory | null;
  deactivated_reason: 'partner_status' | 'owner' | 'replaced' | null;
  source?: 'auto' | 'custom' | null;
  is_current?: boolean;
  replaced_at?: string | null;
  replaces_id?: string | null;

  // Compatibility / UI helper fields
  discountType?: 'flat' | 'percentage';
  discountValue?: number;
  usedCount?: number;
  usageLimit?: number | null;
  startDate?: string;
  endDate?: string;
  isActive?: boolean;
  applicablePlans?: string[];
  description?: string;
  minOrderValue?: number;
  maxDiscount?: number;
}

// Nothing deactivates a code when its expiry_date passes, so "expired" is worked out here.
// expiry_date is the last valid day, in India time.
export const isPromoExpired = (promo: Pick<PromoCodeItem, 'expiry_date'>) =>
  !!promo.expiry_date && promo.expiry_date < todayInIndia();

export interface PromoCodeState {
  promoCodes: PromoCodeItem[];
  selectedPromoCode: PromoCodeItem | null;
  filterStatus: 'all' | 'active' | 'expired';
  loading: boolean;
  error: string | null;
}

export const normalizePromoCode = (item: any): PromoCodeItem => {
  const is_active =
    item.is_active !== undefined
      ? Boolean(item.is_active)
      : item.isActive !== undefined
      ? Boolean(item.isActive)
      : true;

  const rawVal =
    item.discount_value !== undefined
      ? item.discount_value
      : item.discountValue !== undefined
      ? item.discountValue
      : 0;

  const numVal = typeof rawVal === 'string' ? parseFloat(rawVal) || 0 : Number(rawVal) || 0;
  const discount_type =
    item.discount_type || (item.discountType === 'fixed' ? 'flat' : item.discountType) || 'percentage';

  const applicable_plan_ids = Array.isArray(item.applicable_plan_ids)
    ? item.applicable_plan_ids
    : Array.isArray(item.applicablePlans)
    ? item.applicablePlans.filter((p: string) => p !== 'ALL')
    : [];

  const max_usage_count =
    item.max_usage_count !== undefined
      ? item.max_usage_count
      : item.usageLimit !== undefined
      ? item.usageLimit
      : null;

  const usage_count =
    item.usage_count !== undefined
      ? Number(item.usage_count)
      : item.usedCount !== undefined
      ? Number(item.usedCount)
      : 0;

  const expiry_date = item.expiry_date || item.endDate || '2026-12-31';

  return {
    id: String(item.id || ''),
    code: String(item.code || '').trim().toUpperCase(),
    discount_type,
    discount_value: rawVal,
    applicable_plan_ids,
    max_usage_count,
    per_user_limit: Number(item.per_user_limit || 1),
    expiry_date,
    is_active,
    usage_count,
    created_at: item.created_at || item.createdDate || new Date().toISOString(),
    updated_at: item.updated_at || new Date().toISOString(),

    partner_id: item.partner_id ?? null,
    channel: item.channel ?? null,
    category: item.category ?? null,
    deactivated_reason: item.deactivated_reason ?? null,
    source: item.source ?? null,
    is_current: item.is_current !== undefined ? Boolean(item.is_current) : true,
    replaced_at: item.replaced_at ?? null,
    replaces_id: item.replaces_id ?? null,

    // UI helpers
    isActive: is_active,
    discountType: discount_type,
    discountValue: numVal,
    usedCount: usage_count,
    usageLimit: max_usage_count,
    endDate: expiry_date,
    startDate: item.startDate || (item.created_at ? item.created_at.split('T')[0] : '2026-01-01'),
    applicablePlans: applicable_plan_ids.length > 0 ? applicable_plan_ids : ['ALL'],
    description: item.description || '',
    minOrderValue: item.minOrderValue || 0,
    maxDiscount: item.maxDiscount || 0,
  };
};

const initialPromoCodes: PromoCodeItem[] = [];

const initialState: PromoCodeState = {
  promoCodes: initialPromoCodes,
  selectedPromoCode: null,
  filterStatus: 'all',
  loading: false,
  error: null,
};

const promoCodeSlice = createSlice({
  name: 'promoCodes',
  initialState,
  reducers: {
    setPromoCodeLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setPromoCodeError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setPromoCodes: (state, action: PayloadAction<PromoCodeItem[]>) => {
      state.promoCodes = (action.payload || []).map(normalizePromoCode);
    },
    setFilterStatus: (state, action: PayloadAction<'all' | 'active' | 'expired'>) => {
      state.filterStatus = action.payload;
    },
    addPromoCodeReducer: (state, action: PayloadAction<PromoCodeItem>) => {
      const normalized = normalizePromoCode(action.payload);
      const existingIdx = state.promoCodes.findIndex((p) => p.id === normalized.id);
      if (existingIdx !== -1) {
        state.promoCodes[existingIdx] = normalized;
      } else {
        state.promoCodes.unshift(normalized);
      }
    },
    updatePromoCodeReducer: (state, action: PayloadAction<PromoCodeItem>) => {
      const normalized = normalizePromoCode(action.payload);
      const idx = state.promoCodes.findIndex((p) => p.id === normalized.id);
      if (idx !== -1) {
        state.promoCodes[idx] = normalized;
      } else {
        state.promoCodes.unshift(normalized);
      }
      if (state.selectedPromoCode?.id === normalized.id) {
        state.selectedPromoCode = normalized;
      }
    },
    togglePromoCodeStatusReducer: (
      state,
      action: PayloadAction<{ id: string; isActive?: boolean; is_active?: boolean }>
    ) => {
      const newStatus =
        action.payload.is_active !== undefined
          ? Boolean(action.payload.is_active)
          : Boolean(action.payload.isActive);

      const promo = state.promoCodes.find((p) => p.id === action.payload.id);
      if (promo) {
        promo.is_active = newStatus;
        promo.isActive = newStatus;
        promo.updated_at = new Date().toISOString();
      }
      if (state.selectedPromoCode && state.selectedPromoCode.id === action.payload.id) {
        state.selectedPromoCode.is_active = newStatus;
        state.selectedPromoCode.isActive = newStatus;
        state.selectedPromoCode.updated_at = new Date().toISOString();
      }
    },
  },
});

export const {
  setPromoCodeLoading,
  setPromoCodeError,
  setPromoCodes,
  setFilterStatus,
  addPromoCodeReducer,
  updatePromoCodeReducer,
  togglePromoCodeStatusReducer,
} = promoCodeSlice.actions;

export default promoCodeSlice.reducer;
